import { computed, ComputedRef, Ref, ref, watch } from "vue";

import { createDefaultLogger } from "@/types/console-logger";

import { DateTime, Interval } from "luxon";

import {
    DateTimeLike,
    formatDateAsISOString,
    formatDateAsMonthDayYearString,
    localNow,
    maxDateTime,
    normalizeDateTime
} from "@tmi/utils/date-time";

/*********************************************************************/
//                       NON-REACTIVE CONSTANTS                      //
/*********************************************************************/
const logger = createDefaultLogger();

// Used for creating the place-holder text in the date range text input
const dateRangeKeyword: string = ' to ';
// The "final" date is chronological and by default is the current date
const defaultFinalDateTime: DateTime = localNow();
const defaultFinalJSDate: Date = defaultFinalDateTime.toJSDate();
// The "initial" date is the first date of the date interval filter
const defaultInitialDateTime: DateTime = defaultFinalDateTime.minus( { years: 5 } );
const defaultInitialJSDate: Date = defaultInitialDateTime.toJSDate();

const currentDateString: string = formatDateAsISOString( defaultFinalDateTime );

/*********************************************************************/
//                 GLOBALLY SHARED REACTIVE VARIABLES                //
/*********************************************************************/

// The final & initial search dates are hidden variables that are used to trigger the date range filtering
const finalSearchJSDate: Ref< Date > = ref( defaultFinalJSDate );
const finalSearchDateTime = computed< DateTime >( () => normalizeDateTime( finalSearchJSDate.value.toISOString() ) );
const initialSearchJSDate: Ref< Date > = ref( defaultInitialJSDate );
const initialSearchDateTime = computed< DateTime >( () => normalizeDateTime( initialSearchJSDate.value.toISOString() ) );

// The datesFilter is used as the v-model for the calendar range control, the event handler
// updates the final/initial search date reactive variables (which indirectly updates validDateInterval)
const datesFilter: Ref< Date[] > = ref( [ initialSearchJSDate.value, finalSearchJSDate.value ] );
// Piece together the placeholder text for the text input control
const dateRangeText: ComputedRef< string > = computed( () => !!( datesFilter.value ) ? datesFilter.value.join( dateRangeKeyword ) : 'Date Range' );
// This variable is what tracks and is used to filter the actual content based on the Date Range filter
const validDateInterval: ComputedRef< Interval > = computed( () => Interval.fromDateTimes( initialSearchDateTime.value, finalSearchDateTime.value ) );
// The following variable makes the date filtering code a little more readable
const searchIntervalIsDefinedAndValid: ComputedRef< boolean > =
    computed( () =>
        !!validDateInterval.value &&
        ( validDateInterval.value.start?.isValid ?? false ) &&
        ( validDateInterval.value.end?.isValid ?? false ) );

/*********************************************************************/
//                      INSTANCE SPECIFIC STATE
/*********************************************************************/
export function useDateRangeFilter() {
    function clearDateRangeFilter() {
        // The following two implicitly reset the search interval reactive component
        finalSearchJSDate.value = defaultFinalJSDate;
        initialSearchJSDate.value = defaultInitialJSDate;
    }

    // Track the user's input from the calendar selector
    watch ( datesFilter, ( newValueList: Date[] | null ): void => {
        // PrimeVue's DatePicker emits null when the user clears the range. Before this
        // guard that threw on `.length` and left the HIDDEN initial/final dates at their
        // last value — an invisible, stale filter. A cleared picker now means "no range".
        if ( !Array.isArray( newValueList ) || newValueList.length === 0 ) {
            clearDateRangeFilter();
            return;
        }
        if ( newValueList.length > 0 ) {
            initialSearchJSDate.value = newValueList[ 0 ];

            if ( newValueList.length > 1 ) {
                finalSearchJSDate.value = newValueList[ 1 ] ?? initialSearchJSDate.value;
            }

            // Verify that the first Date is before the last, if not then flip them
            if ( finalSearchJSDate.value < initialSearchJSDate.value ) {
                const swap: Date = finalSearchJSDate.value;
                finalSearchJSDate.value = initialSearchJSDate.value;
                initialSearchJSDate.value = swap;
            }

            logger.debug( `[ entry-filters -> watch( datesFilter ) ] dates updated - start: ${ formatDateAsMonthDayYearString( validDateInterval.value.start ) } - end: ${ formatDateAsMonthDayYearString( validDateInterval.value.end ) }` );
        }
    } );

    function isValidDateWithinDateRange( dateTime: DateTimeLike | undefined | null ): boolean {
        const date: DateTime = normalizeDateTime( dateTime );
        const startOfDate: DateTime = date?.startOf( 'day' ) ?? null;
        const publishDateIsDefined: boolean = !!startOfDate;
        const publishDateIsContainedWithinTheSearchInterval: boolean = validDateInterval.value.contains( startOfDate ?? maxDateTime() );

        logger.debug( 'FILTERING on DATE RANGE vs ', startOfDate, ' passes => ', publishDateIsDefined && searchIntervalIsDefinedAndValid && publishDateIsContainedWithinTheSearchInterval );

        return searchIntervalIsDefinedAndValid && publishDateIsContainedWithinTheSearchInterval;
    }

    return {
        clearDateRangeFilter,
        currentDateString,
        datesFilter,
        dateRangeText,
        initialSearchJSDate,
        isValidDateWithinDateRange,
        finalSearchJSDate,
        searchIntervalIsDefinedAndValid,
        validDateInterval,
        defaultInitialJSDate,
        defaultFinalJSDate
    };
}
